import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import {
  ANON_USER_ID,
  INGESTION_SCHEMA_VERSION,
  INGESTION_SOURCE_SYSTEM,
  ingestedConversationSchema,
  type EnrichedTurn,
  type GenericToolCall,
  type IngestedConversation,
} from './schema';

/**
 * Positive eligibility proof for the frozen Pangea ingestion boundary.
 * Non-agent/native conversations cannot produce this value by omission:
 * the trusted source selector must match all policy literals explicitly.
 */
export const PANGEA_SOURCE_POLICY = {
  sourceType: 'voice_agent',
  classification: 'agent',
  status: 'completed',
  egressPolicy: 'pangea_ingestion_v1',
} as const;

export interface PangeaPolicyCandidate {
  sourceType: unknown;
  classification: unknown;
  status: unknown;
  egressPolicy: unknown;
}

export function hasPangeaEgressPolicy(source: PangeaPolicyCandidate): boolean {
  return (
    source.sourceType === PANGEA_SOURCE_POLICY.sourceType &&
    source.classification === PANGEA_SOURCE_POLICY.classification &&
    source.status === PANGEA_SOURCE_POLICY.status &&
    source.egressPolicy === PANGEA_SOURCE_POLICY.egressPolicy
  );
}

export const pangeaEligibleSourceSchema = z
  .object({
    internalConversationId: z.string().uuid(),
    sourceId: z.string().uuid(),
    sourceType: z.literal(PANGEA_SOURCE_POLICY.sourceType),
    classification: z.literal(PANGEA_SOURCE_POLICY.classification),
    status: z.literal(PANGEA_SOURCE_POLICY.status),
    egressPolicy: z.literal(PANGEA_SOURCE_POLICY.egressPolicy),
    provider: z.string().min(1),
    externalConversationId: z.string().min(1),
    externalAgentId: z.string().min(1).nullable(),
    memberId: memberIdSchema.nullable(),
    memberIdentityCount: z.union([z.literal(0), z.literal(1)]),
  })
  .strict()
  .superRefine((source, ctx) => {
    if ((source.memberIdentityCount === 0) !== (source.memberId === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['memberId'],
        message: 'member identity count must agree with the persisted member id',
      });
    }
  })
  .brand<'PangeaEligibleSource'>();
export type PangeaEligibleSource = z.infer<typeof pangeaEligibleSourceSchema>;

export function selectPangeaEligibleSource(input: unknown): PangeaEligibleSource {
  return pangeaEligibleSourceSchema.parse(input);
}

/**
 * Inputs for one `IngestedConversation` document. The producer supplies the
 * already-projected `transcript` and the call context; product-policy values
 * (`agentType` / `conversationType`) are the caller's decision — this builder
 * stays free of Galapagos-specific defaults. Optional `toolCalls` / `summary` /
 * `promptParams` are included only when provided (omitted for v1).
 */
export interface BuildIngestedConversationInput {
  /** Proof returned by the positive source-policy selector. */
  source: PangeaEligibleSource;
  /** Opaque source-provided agent family (not Pangea's enum). */
  agentType: string;
  /** Opaque source-provided conversation type; drives PangeaBench's badge. */
  conversationType: string;
  language?: string | null;
  durationSec?: number;
  transcript: EnrichedTurn[];
  /** Idempotency counter with `conversationId`; first document is `0`. */
  revision?: number;
  toolCalls?: GenericToolCall[];
  summary?: string | null;
  promptParams?: Record<string, unknown> | null;
}

/**
 * Project a processed conversation into Pangea's `IngestedConversation` wire
 * contract. Pure and total: `agent_id` / `user_id` fall back to the empty
 * string the contract's non-null strings require, `duration_sec` to 0, and
 * `language` to null. The result is validated through `ingestedConversationSchema`
 * before returning — a shape the contract would reject throws here, at the
 * producer, rather than landing a bad object for Pangea.
 */
export function buildIngestedConversation(
  input: BuildIngestedConversationInput,
): IngestedConversation {
  // Re-validate at the final adapter boundary as defense in depth against an
  // `as` cast or untyped caller bypassing the branded selector.
  const eligibleSource = pangeaEligibleSourceSchema.parse(input.source);
  const doc: IngestedConversation = {
    context: {
      agent_id: eligibleSource.externalAgentId ?? '',
      agent_type: input.agentType,
      conversation_id: eligibleSource.externalConversationId,
      user_id: eligibleSource.memberId ?? ANON_USER_ID,
      language: input.language ?? null,
      duration_sec: input.durationSec ?? 0,
    },
    transcript: input.transcript,
    source_meta: {
      schema_version: INGESTION_SCHEMA_VERSION,
      source_system: INGESTION_SOURCE_SYSTEM,
      conversation_type: input.conversationType,
      voice_provider_conversation_id: eligibleSource.externalConversationId,
      revision: input.revision ?? 0,
    },
  };

  if (input.toolCalls !== undefined) doc.tool_calls = input.toolCalls;
  if (input.summary !== undefined) doc.summary = input.summary;
  if (input.promptParams !== undefined) doc.source_meta.prompt_params = input.promptParams;

  return ingestedConversationSchema.parse(doc);
}
